/**
 * Turns a raw outbound webhook URL into a friendly "destination" label for the
 * dashboard so users see where a trade was sent (e.g. "TradersPost") instead of
 * a long URL that may contain tokens/secrets.
 */

// Well-known trading / notification webhook hosts mapped to display names.
const KNOWN_HOSTS: Record<string, string> = {
  'traderspost.io': 'TradersPost',
  'optionsalpha.com': 'Options Alpha',
  'discord.com': 'Discord',
  'discordapp.com': 'Discord',
  'slack.com': 'Slack',
  'hooks.slack.com': 'Slack',
  'api.telegram.org': 'Telegram',
  'zapier.com': 'Zapier',
  'hooks.zapier.com': 'Zapier',
  'make.com': 'Make',
  'hook.us1.make.com': 'Make',
  'hook.eu1.make.com': 'Make',
  'ifttt.com': 'IFTTT',
  'maker.ifttt.com': 'IFTTT',
  'pipedream.net': 'Pipedream',
  'webhook.site': 'Webhook.site',
  'n8n.io': 'n8n',
};

function matchKnownHost(host: string): string | null {
  if (KNOWN_HOSTS[host]) return KNOWN_HOSTS[host];
  for (const [knownHost, name] of Object.entries(KNOWN_HOSTS)) {
    if (host === knownHost || host.endsWith(`.${knownHost}`)) return name;
  }
  return null;
}

/**
 * Resolve a human-friendly destination name from a webhook URL. Known services
 * get a proper name; anything else falls back to the bare hostname (never the
 * query string, which can carry secrets).
 */
export function formatWebhookDestination(url?: string | null): string {
  const raw = (url ?? '').trim();
  if (!raw) return 'No destination set';
  try {
    const parsed = new URL(raw);
    const host = parsed.hostname.replace(/^www\./, '').toLowerCase();
    return matchKnownHost(host) ?? host;
  } catch {
    // Not a parseable URL — strip any query string and truncate for display.
    const withoutQuery = raw.split('?')[0];
    return withoutQuery.length > 48 ? `${withoutQuery.slice(0, 48)}…` : withoutQuery;
  }
}
